import { useState, useCallback } from 'react'
import {
  startConversation,
  sendExchange,
  endConversation,
  StartConversationResponse,
  SendExchangeResponse,
  EndConversationResponse,
} from '../services/api'
import { useConversationStore } from '../store/conversationStore'

export interface UseConversationReturn {
  isLoading: boolean
  error: string | null
  currentSession: any
  exchanges: any[]
  startSession: (
    userId: string,
    difficultyLevel: 'beginner' | 'intermediate' | 'advanced',
    topic?: string,
    aiPersonality?: string,
    targetLanguage?: string
  ) => Promise<StartConversationResponse | null>
  sendMessage: (
    sessionId: number,
    audio?: Blob,
    text?: string,
    aiPersonality?: string
  ) => Promise<SendExchangeResponse | null>
  endSession: (
    sessionId: number,
    status?: 'completed' | 'interrupted',
    localDate?: string
  ) => Promise<EndConversationResponse | null>
  clearError: () => void
}

export function useConversation(): UseConversationReturn {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    currentSession,
    exchanges,
    startSession: storeStartSession,
    addExchange,
    endSession: storeEndSession,
    setWaitingForAI,
    setError: storeSetError,
    reset,
    setUsageLimitReached,
  } = useConversationStore()

  // Start a new conversation session
  const startSession = useCallback(
    async (
      userId: string,
      difficultyLevel: 'beginner' | 'intermediate' | 'advanced',
      topic?: string,
      aiPersonality?: string,
      targetLanguage?: string
    ): Promise<StartConversationResponse | null> => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await startConversation({
          userId,
          difficultyLevel,
          requestedTopic: topic,
          aiPersonality,
          targetLanguage,
        })

        // Update store
        storeStartSession(response.session as any)

        // Add AI greeting as first exchange
        addExchange({
          id: 0,
          sessionId: response.session.id,
          sequenceNumber: 1,
          speaker: 'ai',
          textContent: response.aiGreeting.text,
          audioUrl: response.aiGreeting.audioUrl,
          timestamp: new Date().toISOString(),
        } as any)

        return response
      } catch (err) {
        console.error('Conversation startSession error:', err)
        const message = err instanceof Error ? err.message : 'Failed to start conversation'
        setError(message)
        storeSetError(message)
        return null
      } finally {
        setIsLoading(false)
      }
    },
    [storeStartSession, addExchange, storeSetError]
  )

  // Send user message and get AI response
  const sendMessage = useCallback(
    async (
      sessionId: number,
      audio?: Blob,
      text?: string,
      aiPersonality?: string
    ): Promise<SendExchangeResponse | null> => {
      setIsLoading(true)
      setWaitingForAI(true)
      setError(null)

      try {
        const response = await sendExchange(sessionId, audio, text, aiPersonality)

        // Add user exchange
        addExchange({
          id: response.userExchange.id,
          sessionId,
          sequenceNumber: exchanges.length + 1,
          speaker: 'user',
          textContent: response.userExchange.text,
          transcriptionConfidence: response.userExchange.confidenceScore,
          pronunciationFeedback: response.userExchange.pronunciationFeedback || null,
          learningNotes: response.userExchange.learningNotes || null,
          timestamp: response.userExchange.timestamp,
        } as any)

        // Add AI exchange
        addExchange({
          id: response.aiExchange.id,
          sessionId,
          sequenceNumber: exchanges.length + 2,
          speaker: 'ai',
          textContent: response.aiExchange.text,
          audioUrl: response.aiExchange.audioUrl,
          timestamp: response.aiExchange.timestamp,
        } as any)

        return response
      } catch (err: any) {
        console.error('Conversation sendMessage error:', err)

        // Check if it's a usage limit error
        let message = 'Failed to send message'
        const errorCode = (err as any)?.code
        if (errorCode === 'USAGE_LIMIT_EXCEEDED') {
          setUsageLimitReached(true)
          message = (err as Error).message || 'Daily AI response limit reached.'
        } else if (err?.message?.includes('limit') || err?.message?.includes('Daily')) {
          setUsageLimitReached(true)
          message = err.message + ' Upgrade to Premium for unlimited conversations.'
        } else if (err instanceof Error) {
          message = err.message
        }

        setError(message)
        storeSetError(message)
        return null
      } finally {
        setIsLoading(false)
        setWaitingForAI(false)
      }
    },
    [exchanges.length, addExchange, setWaitingForAI, storeSetError, setUsageLimitReached]
  )

  // End conversation session
  const endSession = useCallback(
    async (
      sessionId: number,
      status: 'completed' | 'interrupted' = 'completed',
      localDate?: string
    ): Promise<EndConversationResponse | null> => {
      setIsLoading(true)
      setError(null)

      try {
        const response = await endConversation(sessionId, status, localDate)

        // Update store
        storeEndSession()

        return response
      } catch (err) {
        console.error('Conversation endSession error:', err)
        const message = err instanceof Error ? err.message : 'Failed to end conversation'
        setError(message)
        storeSetError(message)
        return null
      } finally {
        setIsLoading(false)
      }
    },
    [storeEndSession, storeSetError]
  )

  // Clear error
  const clearError = useCallback(() => {
    setError(null)
    storeSetError(null)
  }, [storeSetError])

  return {
    isLoading,
    error,
    currentSession,
    exchanges,
    startSession,
    sendMessage,
    endSession,
    clearError,
  }
}
